
    import { computed, ref, defineComponent } from 'vue'
    import { Route } from '../enums/Route'
    import { Theme } from '../enums/Theme'
    import PartnersNew from '../components/PartnersNew.vue'
    import ClippingAnimation from '../components/ClippingAnimation.vue'
    import CustomCursor from '../components/CustomCursor.vue'
    import Picture from '../components/Picture.vue'
    import Anchor from '../components/Anchor.vue'
    import { useCms } from '../compositions/UseCms'
    import fallbackCaseStudies from '../cms/cn/en/cases.json'

    interface Tab {
        id: string
        label: string
    }

    export default defineComponent({
        name: 'HomeNew',
        components: {
            PartnersNew,
            ClippingAnimation,
            CustomCursor,
            Picture,
            Anchor
        },
        setup() {

            const { caseStudies, home } = useCms()
            const activeTab = ref('featured')

            const tabs: Tab[] = [
                { id: 'featured', label: 'Featured Work' },
                { id: 'consumer', label: 'Consumer Apps' },
                { id: 'enterprise', label: 'Enterprise Platforms' }
            ]

            const projects = computed(() => {

                const cmsProjects = caseStudies.value || home.value?.related_cases || []

                return cmsProjects.length ? cmsProjects : fallbackCaseStudies

            })

            const complianceItems = [
                'ISO 27001 Certified',
                'ISO 9001 Certified',
                'PIPL Compliant',
                'MLPS Ready',
                'ICP Licensing Support'
            ]

            const complianceBadges = [
                {
                    title: 'ISO<br/>27001<br/>Certified',
                    description: 'Information security<br/>and data protection<br/>standards'
                },
                {
                    title: 'ISO<br/>9001<br/>Certified',
                    description: 'Quality management<br/>and process<br/>standards'
                }
            ]

            const capabilities = [
                {
                    title: 'Strategy',
                    description: 'Business, product, and technology strategy including digital roadmaps, platform strategy, product definition, and long-term technology planning aligned to business goals.'
                },
                {
                    title: 'User Research',
                    description: 'User research, stakeholder interviews, usability testing, audits, and discovery work that inform product direction, platform design, and digital strategy decisions.'
                },
                {
                    title: 'Product Design',
                    description: 'UX and UI design for platforms, products, and applications, including user flows, wireframes, design systems, and scalable interface design across devices.'
                },
                {
                    title: 'Engineering',
                    description: 'Frontend, backend, and full-stack engineering for platforms, products, and systems, including web applications, mobile apps, mini programs, and cloud-based platforms.'
                },
                {
                    title: 'AI & Data',
                    description: 'AI strategy, data platforms, automation, and AI-powered product features, including LLM integrations, data pipelines, analytics platforms, and intelligent systems.'
                },
                {
                    title: 'Integrations & APIs',
                    description: 'System integration, API design, and middleware development that connect platforms, products, CRMs, ERPs, and third-party services across digital ecosystems.'
                },
                {
                    title: 'System Architecture',
                    description: 'System architecture design including platform architecture, data architecture, infrastructure planning, and technology stack design for scalable digital systems.'
                },
                {
                    title: 'Security & Compliance',
                    description: 'Security architecture, data protection, compliance, hosting strategy, and technology governance to ensure platforms and systems are secure and compliant.'
                },
                {
                    title: 'Documentation',
                    description: 'Technical, documentation, and product documentation, and internal knowledge systems that support development, operations, and long-term platform management.'
                },
                {
                    title: 'Maintenance & Ops',
                    description: 'Platform maintenance, system operations, monitoring, updates, performance optimization, and ongoing support for digital platforms, products, and systems.'
                }
            ]

            const filteredProjects = computed(() => {

                return projects.value

            })

            const featuredProject = computed(() => {

                return filteredProjects.value[0]

            })

            const remainingProjects = computed(() => {

                return filteredProjects.value.slice(1)

            })

            return {
                Route,
                Theme,
                activeTab,
                tabs,
                projects,
                filteredProjects,
                featuredProject,
                remainingProjects,
                capabilities,
                complianceItems,
                complianceBadges
            }

        }
    })
